import { FormEvent, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, post, useData, useSession } from '@yingbaba/api';
import {
  Select,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Modal,
  Notice,
  PageTitle,
  Pagination,
  QueryState,
  Status,
  useToast,
} from '@yingbaba/ui';
import { datetime } from '@yingbaba/utils';
import { Plus, ShieldCheck, UserRoundCog } from 'lucide-react';

type PlatformRole =
  'operator' | 'reviewer' | 'finance' | 'customer_service' | 'auditor' | 'super_admin';
type PlatformMember = {
  account_id: string;
  name: string;
  email: string;
  platform_role: PlatformRole | 'user';
  status: string;
  last_admin_login_at: string;
};
type PlatformMemberPage = {
  items: PlatformMember[];
  total: number;
  page: number;
  page_size: number;
  super_admin_count: number;
};

const roles: { value: PlatformRole; label: string; description: string; highRisk?: boolean }[] = [
  { value: 'operator', label: 'Operator', description: '平台日常运营' },
  { value: 'reviewer', label: 'Reviewer', description: '服务商 / 内容 / 需求审核' },
  { value: 'finance', label: 'Finance', description: '付款 / 佣金 / 结算', highRisk: true },
  { value: 'customer_service', label: 'Customer Service', description: '用户咨询与项目沟通支持' },
  { value: 'auditor', label: 'Auditor', description: '只读审计' },
  { value: 'super_admin', label: 'Super Admin', description: '平台最高管理权限', highRisk: true },
];
const roleLabel = (role: string) => roles.find((item) => item.value === role)?.label || role;

export function PlatformMembers() {
  const session = useSession();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const path = `/admin/platform-members?page=${page}&page_size=20${query ? `&q=${encodeURIComponent(query)}` : ''}`;
  const members = useData<PlatformMemberPage>(path);
  const superAdminCount = useMemo(() => members.data?.super_admin_count || 0, [members.data]);

  return (
    <>
      <PageTitle
        title="运营人员"
        description="仅 Super Admin 可管理平台运营角色。角色变化会立即撤销目标账号全部管理后台会话，并写入审计日志。"
        action={<AddPlatformMember onChanged={() => qc.invalidateQueries()} />}
      />
      {members.data && members.data.total > 0 && superAdminCount < 2 ? (
        <Notice>
          当前共有 {superAdminCount} 名 Super Admin。生产环境建议至少保留 2
          名联合管理员，避免单一账号锁死。
        </Notice>
      ) : null}
      <form
        className="filter-bar"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(search.trim());
          setPage(1);
        }}
      >
        <input
          className="input"
          aria-label="搜索运营人员"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="搜索姓名或邮箱"
        />
        <Button variant="secondary">搜索</Button>
        <span className="result-count">共 {members.data?.total ?? '…'} 人</span>
      </form>
      <QueryState query={members} empty={!members.data?.items.length}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>姓名</th>
                <th>邮箱</th>
                <th>平台角色</th>
                <th>状态</th>
                <th>最近登录</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {members.data?.items.map((member) => (
                <tr key={member.account_id}>
                  <td>
                    <strong>{member.name}</strong>
                  </td>
                  <td>{member.email}</td>
                  <td>
                    <span className="tag">{roleLabel(member.platform_role)}</span>
                  </td>
                  <td>
                    <Status value={member.status === 'active' ? '正常' : member.status} />
                  </td>
                  <td>
                    {member.last_admin_login_at
                      ? datetime(member.last_admin_login_at)
                      : '从未登录后台'}
                  </td>
                  <td>
                    <div className="table-actions">
                      <ChangeRole
                        member={member}
                        currentAccountID={session.data?.account.id || ''}
                        onChanged={() => qc.invalidateQueries()}
                      />
                      <RemoveMember
                        member={member}
                        currentAccountID={session.data?.account.id || ''}
                        onChanged={() => qc.invalidateQueries()}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
      <Pagination page={page} total={members.data?.total || 0} size={20} onPage={setPage} />
    </>
  );
}

function AddPlatformMember({ onChanged }: { onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<PlatformMember[]>([]);
  const [selected, setSelected] = useState('');
  const [role, setRole] = useState<PlatformRole>('operator');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();

  async function searchAccounts(event: FormEvent) {
    event.preventDefault();
    if (query.trim().length < 2) return;
    setSearching(true);
    setError(null);
    try {
      const page = await api<PlatformMemberPage>(
        `/admin/platform-members?candidates=1&page=1&page_size=20&q=${encodeURIComponent(query.trim())}`,
      );
      setCandidates(page.items);
      setSelected(page.items[0]?.account_id || '');
    } catch (e) {
      setError(e);
    } finally {
      setSearching(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await post('/admin/platform-members', { account_id: selected, role, reason });
      onChanged();
      setOpen(false);
      setQuery('');
      setCandidates([]);
      setSelected('');
      setRole('operator');
      setReason('');
      toast('运营人员已添加');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const highRisk = roles.find((item) => item.value === role)?.highRisk;
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        <Plus size={15} /> 添加运营人员
      </Button>
      <Modal
        open={open}
        busy={busy}
        onOpenChange={setOpen}
        title="添加运营人员"
        description="从已有账号中选择。组织角色不会因此变化，平台角色仅影响独立运营后台。"
      >
        <div className="form-stack">
          <form className="row" onSubmit={searchAccounts}>
            <input
              className="input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="输入姓名或邮箱，至少 2 个字符"
              minLength={2}
              required
            />
            <Button variant="secondary" loading={searching}>
              搜索账号
            </Button>
          </form>
          {candidates.length ? (
            <form className="form-stack" onSubmit={submit}>
              <Field htmlFor="platform-account" label="用户">
                <Select
                  id="platform-account"
                  value={selected}
                  onChange={(event) => setSelected(event)}
                  required
                >
                  {candidates.map((candidate) => (
                    <option key={candidate.account_id} value={candidate.account_id}>
                      {candidate.name} · {candidate.email}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                htmlFor="platform-role"
                label="平台角色"
                hint={highRisk ? '这是高风险角色，必须明确记录授权原因。' : undefined}
              >
                <Select
                  id="platform-role"
                  value={role}
                  onChange={(event) => setRole(event as PlatformRole)}
                >
                  {roles.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label} · {item.description}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field htmlFor="platform-reason" label="变更原因">
                <textarea
                  id="platform-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  minLength={2}
                  maxLength={500}
                  required
                />
              </Field>
              <ErrorNotice error={error} />
              <div className="form-actions">
                <Button variant="secondary" type="button" onClick={() => setOpen(false)}>
                  取消
                </Button>
                <Button loading={busy} type="submit">
                  确认添加
                </Button>
              </div>
            </form>
          ) : query.trim().length >= 2 && !searching ? (
            <Empty
              title="没有找到可添加的普通账号"
              description="已是运营人员的账号不会出现在候选结果中。"
            />
          ) : null}
          {!candidates.length ? <ErrorNotice error={error} /> : null}
        </div>
      </Modal>
    </>
  );
}

function ChangeRole({
  member,
  currentAccountID,
  onChanged,
}: {
  member: PlatformMember;
  currentAccountID: string;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<PlatformRole>(member.platform_role as PlatformRole);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const selfSuperAdmin =
    member.account_id === currentAccountID && member.platform_role === 'super_admin';
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/platform-members/${member.account_id}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role, reason }),
      });
      onChanged();
      setOpen(false);
      setReason('');
      toast('平台角色已更新，目标账号管理会话已撤销');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <UserRoundCog size={14} /> 修改角色
      </Button>
      <Modal
        open={open}
        busy={busy}
        onOpenChange={setOpen}
        title={`修改 ${member.name} 的平台角色`}
        description="保存后会立即撤销该账号所有管理后台 Session，用户需要重新登录。"
      >
        <form className="form-stack" onSubmit={submit}>
          {selfSuperAdmin ? (
            <Notice>为避免误锁账号，不能直接取消或降级自己的 Super Admin 角色。</Notice>
          ) : null}
          <Field htmlFor={`role-${member.account_id}`} label="平台角色">
            <Select
              id={`role-${member.account_id}`}
              value={role}
              onChange={(event) => setRole(event as PlatformRole)}
            >
              {roles.map((item) => (
                <option
                  key={item.value}
                  value={item.value}
                  disabled={selfSuperAdmin && item.value !== 'super_admin'}
                >
                  {item.label} · {item.description}
                </option>
              ))}
            </Select>
          </Field>
          <Field htmlFor={`reason-${member.account_id}`} label="变更原因">
            <textarea
              id={`reason-${member.account_id}`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              minLength={2}
              maxLength={500}
              required
            />
          </Field>
          <ErrorNotice error={error} />
          <div className="form-actions">
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button loading={busy} type="submit" disabled={role === member.platform_role}>
              确认修改
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

function RemoveMember({
  member,
  currentAccountID,
  onChanged,
}: {
  member: PlatformMember;
  currentAccountID: string;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const protectedSelf =
    member.account_id === currentAccountID && member.platform_role === 'super_admin';
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/platform-members/${member.account_id}`, {
        method: 'DELETE',
        body: JSON.stringify({ reason }),
      });
      onChanged();
      setOpen(false);
      setReason('');
      toast('已取消平台角色，目标账号管理会话已撤销');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} disabled={protectedSelf}>
        移除
      </Button>
      <Modal
        open={open}
        busy={busy}
        onOpenChange={setOpen}
        title={`移除运营人员 ${member.name}`}
        description="账号本身不会删除，也不会改变任何组织成员角色；仅恢复为普通平台用户。"
      >
        <form className="form-stack" onSubmit={submit}>
          <Notice tone="error">
            移除后，该账号所有管理后台 Session 会立即失效。最后一名 Super Admin
            受后端硬保护，无法移除。
          </Notice>
          <Field htmlFor={`remove-reason-${member.account_id}`} label="变更原因">
            <textarea
              id={`remove-reason-${member.account_id}`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              minLength={2}
              maxLength={500}
              required
            />
          </Field>
          <ErrorNotice error={error} />
          <div className="form-actions">
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button loading={busy} type="submit">
              确认移除
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
